import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from '@simplybenefits/sb-dates';

import {
    Button, Loader, Text, Services, Theme, MessageCard, Modal, AmendmentDisplay, ContainerControlSite, Content, ContentBlock
} from '@simplybenefits/sb-ui-library';

import Header from '../../../components/Header.js';
import AmendmentForm from './AmendmentForm.js';

import { getAccount, getAmendment, saveAmendment, createRevision, sendAmendmentForApproval } from '../../../actions/accounts.js';
import { getPdf } from '../../../actions/master.js';

class Amendment extends Component {
    state = {
        amendment: null,
        effectiveDateError: false
    };

    async componentDidMount() {
        if (!this.props.currentAccountMeta) {
            await this.props.dispatch(getAccount(this.props.match.params.accountID));
        }

        if (!this.state.amendment) {
            const amendment = await this.props.dispatch(getAmendment(this.props.match.params.accountID, this.props.match.params.amendmentID));
            amendment.effectiveDate = amendment.effectiveDate ? dates.getUTC(amendment.effectiveDate, 'YYYY/MM/DD') : dates.getUTC(null, 'YYYY/MM/DD');

            if (amendment) {
                this.setState({
                    amendment
                });
            }
        }
    }

    _buildBreadcrumbs = () => [
        {
            title: 'Accounts',
            link: `/accounts`
        },
        {
            title: this.props.currentAccountMeta.company,
            link: `/accounts/${ this.props.currentAccountMeta.accountID }`
        },
        {
            title: 'Amendments',
            link: `/accounts/${ this.props.currentAccountMeta.accountID }/amendments`
        }
    ];

    _validateEffectiveDate = async () => {
        let effectiveDateError = await Services.validateInput('effectiveDate', this.state.amendment.effectiveDate);

        if (this.state.amendment.status.toUpperCase() === 'IN PROGRESS') {
            const startOfMonth = dates.getStartOfMonth();
            const effectiveDateUTC = dates.getUTC(this.state.amendment.effectiveDate);

            if (effectiveDateUTC < startOfMonth) {
                effectiveDateError = 'Amendments can only be backed dated to the start of the month.';
            }
        }

        await this.setState({
            effectiveDateError
        });

        if (effectiveDateError) {
            return false;
        }
        
        return true;
    };

    _handleChange = (type, benefitKey, value) => {
        this.setState({
            amendment: {
                ...this.state.amendment,
                requestedBenefitChanges: {
                    ...this.state.amendment.requestedBenefitChanges,
                    [benefitKey]: {
                        ...this.state.amendment.requestedBenefitChanges[benefitKey],
                        [type]: value
                    }
                }
            }
        });
    };

    _sendAmendmentApproval = async () => {
        const isEffectiveDateValid = await this._validateEffectiveDate();

        if (!isEffectiveDateValid) {
            return false;
        }

        const amendment = {
            ...this.state.amendment,
            effectiveDate: dates.getUTC(this.state.amendment.effectiveDate)
        };

        window.scrollTo(0, 0);

        const saveSuccess = await this.props.dispatch(saveAmendment(this.props.match.params.accountID, this.props.match.params.amendmentID, amendment, true));

        if (saveSuccess) {
            this.setState({
                showConfirmDialog: false
            });

            const success = await this.props.dispatch(sendAmendmentForApproval(this.props.match.params.accountID, this.props.match.params.amendmentID));

            if (success) {
                setTimeout(() => {
                    window.location.href = `/accounts/${ this.props.match.params.accountID }/amendments`;
                }, 2000);
            }
        }

        return null;
    };

    _createRevision = async () => {
        const revision = await this.props.dispatch(createRevision(this.props.match.params.accountID, this.props.match.params.amendmentID));

        if (revision) {
            window.location.href = `/accounts/${ this.props.match.params.accountID }/revision/${ revision.revisionID }`;
        }
    };

    _goToRevision = () => {
        window.location.href = `/accounts/${ this.props.match.params.accountID }/revision/${ this.state.amendment.revisionID }`;
    };

    _downloadAmendmentPDF = async () => {
        const accountID = this.props.match.params.accountID;
        const amendmentID = this.props.match.params.amendmentID;
        const fileName = `amendment_${ this.props.match.params.amendmentID }.pdf`;
        await this.props.dispatch(getPdf("Amendment", fileName, { accountID, amendmentID }));
    };

    render() {
        const { currentAccountMeta } = this.props;

        if (!currentAccountMeta || !this.state.amendment) {
            return <Loader />;
        }

        const breadcrumbs = this._buildBreadcrumbs();
        const amendmentStatus = this.state.amendment.status.toUpperCase();

        const styles = {
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                paddingBottom: 20
            },
            block: {
                border: "1.5px solid #F1F1F1",
                paddingLeft: 30,
                paddingRight: 30,
                borderRadius: 5,
                marginTop: 20,
                backgroundColor: '#FFFFFF'
            },
            header: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: "space-between"
            },
            headerBorder: {
                borderBottom: "1.5px solid #F1F1F1"
            },
            blockContent: {
                paddingTop: 20,
                paddingBottom: 50
            },
            signageAcceptBox: {
                paddingTop: '0',
                marginTop: '-15px'
            },
            signature: {
                fontFamily: 'AntaraDistance',
                fontWeight: 'bold'
            }
        };

        return (
            <ContainerControlSite>
                <Header
                    subHeader="Amendment"
                    breadcrumbs={breadcrumbs}
                    selectedTitle="accounts"
                />

                <Content>
                    <Text
                        align="right"
                        bold
                    >
                        { currentAccountMeta.company } ({ currentAccountMeta.groupNum })
                    </Text>

                    <Text
                        align="right"
                        light
                    >
                        Advisor: { currentAccountMeta.advisor }
                    </Text>

                    {
                        currentAccountMeta.effDate &&
                        <Text
                            align="right"
                            light
                        >
                            Effective Date: { dates.getStartOfDay(currentAccountMeta.effDate, 'll') }
                        </Text>
                    }

                    <Text
                        align="right"
                        light
                    >
                        Secondary Policy #: { currentAccountMeta.secondaryPolicyNumber || 'None' }
                    </Text>

                    {
                        amendmentStatus === 'IN PROGRESS' &&
                            <MessageCard
                                title="In Progress"
                                subtitle="Use the form below to outline the requested changes to the policy. Once completed, use the button below to send the Amendment for approval to the policy Signing Authority."
                            />
                    }

                    {
                        amendmentStatus === 'UNDER REVIEW' &&
                            <MessageCard
                                title="Amendment Form Under Review"
                                subtitle="The Amendment has already been sent to the Signing Authority. Use the button below to send the Amendment for review again."
                            />
                    }

                    {
                        amendmentStatus === 'APPROVED' &&
                            <MessageCard
                                title="Amendment Form Approved"
                                subtitle="The Amendment has been approved by the Signing Authority. All changes are now final. Please use the button below to start the Revision process for the Amendment."
                            />
                    }

                    {
                        amendmentStatus === 'UNDER REVISION' &&
                            <MessageCard
                                title="Amendment Form Under Revision"
                                subtitle="The Amendment is currently under Revisions. Use the button below to view the Revision dashboard."
                            />
                    }

                    {
                        amendmentStatus === 'PENDING' &&
                            <MessageCard
                                title="Amendment Pending"
                                subtitle={ `The Amendment has been approved and the Revisions have been completed. The changes are pending until the effective date of ${ dates.getUTC(this.state.amendment.effectiveDate, 'll') }.` }
                            />
                    }

                    {
                        amendmentStatus === 'COMPLETE' &&
                            <MessageCard
                                title="Amendment Completed"
                                subtitle="The Amendment has been approved and the Revisions have been completed."
                            />
                    }

                    <ContentBlock style={{ padding: 50 }}>
                        <div style={ styles.titleBlock }>
                            <Text
                                extraLarge
                                veryBold
                                color={ Theme.colors.mainTextGrey }
                                customStyle={{ flex: '1' }}
                            >
                                Amendment Form
                            </Text>

                            {
                                amendmentStatus !== 'IN PROGRESS' &&
                                    <Button
                                        color="white"
                                        noHover
                                        customStyle={{
                                            height: 50,
                                            width: 200,
                                            textTransform: "capitalize",
                                            fontSize: 16
                                        }}
                                        onClick={ this._downloadAmendmentPDF}
                                    >
                                        Download PDF
                                    </Button>
                            }

                            {
                                amendmentStatus === 'IN PROGRESS' &&
                                    <Button
                                        color="primary"
                                        customStyle={{
                                            height: 50,
                                            width: 200,
                                            textTransform: "capitalize",
                                            fontSize: 16,
                                            marginLeft: "15px"
                                        }}
                                        onClick={ () => {
                                            this.setState({
                                                showConfirmDialog: true
                                            });
                                        }}
                                    >
                                        Send for Approval
                                    </Button>
                            }

                            {
                                amendmentStatus === 'UNDER REVIEW' &&
                                    <Button
                                        color="primary"
                                        customStyle={{
                                            height: 50,
                                            width: 200,
                                            textTransform: "capitalize",
                                            fontSize: 16,
                                            marginLeft: "15px"
                                        }}
                                        onClick={ () => {
                                            this.setState({
                                                showConfirmDialog: true
                                            });
                                        }}
                                    >
                                        Send for Approval
                                    </Button>
                            }

                            {
                                amendmentStatus === 'APPROVED' &&
                                    <Button
                                        color="primary"
                                        customStyle={{
                                            height: 50,
                                            width: 200,
                                            textTransform: "capitalize",
                                            fontSize: 16,
                                            marginLeft: "15px"
                                        }}
                                        onClick={ this._createRevision }
                                    >
                                        Start Revisions
                                    </Button>
                            }

                            {
                                amendmentStatus === 'UNDER REVISION' &&
                                    <Button
                                        color="primary"
                                        customStyle={{
                                            height: 50,
                                            width: 200,
                                            textTransform: "capitalize",
                                            fontSize: 16,
                                            marginLeft: "15px"
                                        }}
                                        onClick={ this._goToRevision }
                                    >
                                        Go To Revision
                                    </Button>
                            }
                        </div>

                        {
                            [ 'IN PROGRESS', 'UNDER REVIEW' ].includes(amendmentStatus) ?
                                <AmendmentForm
                                    amendment={ this.state.amendment }
                                    accountID={ this.props.match.params.accountID }
                                    amendmentID={ this.props.match.params.amendmentID }
                                    effectiveDateError={ this.state.effectiveDateError }
                                    saveParentAmendment={(amendment) => {
                                        this.setState({
                                            amendment: {
                                                ...amendment,
                                                effectiveDate: amendment.effectiveDate ? dates.getUTC(amendment.effectiveDate, 'YYYY/MM/DD') : dates.getUTC(null, 'YYYY/MM/DD')
                                            }
                                        });
                                    }}
                                />
                                :
                                <AmendmentDisplay
                                    amendment={ this.state.amendment }
                                    whitelabel={{}}
                                    classData={ this.props.classData }
                                />
                        }

                    </ContentBlock>
                </Content>

                <Modal
                    open={ Boolean(this.state.showConfirmDialog) }
                    onClose={() => {
                        this.setState({
                            showConfirmDialog: false
                        });
                    }}
                    title="Are you sure?"
                    primaryButton={{
                        label: "Send For Approval",
                        onClick: this._sendAmendmentApproval
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        { `By continuing you will be sending the Amendment to the Signing Authority for approval. Once under approval, the amendment will no longer be editable. Please confirm all your changes are correct and that the effective date of: ${ dates.getUTC(this.state.amendment.effectiveDate, 'll') } is correct. ` }
                    </Text>
                </Modal>
            </ContainerControlSite>
        );
    }
}

Amendment.propTypes = {
    currentAccountMeta: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentAccountMeta: state.accounts.currentAccount.meta,
    classData: state.accounts.currentAccount.classes
});

export default (connect(mapStateToProps)(Amendment));